import {
  createSubmitCancelLifecycleContract,
  defineDoctype,
  normalizeLifecycleContract,
  type DoctypeCatalogMetadata,
  type DoctypeChildCascadePolicy,
  type DoctypeDefinition,
  type DocumentLifecycleContract,
} from "@kubdee/sdk";
import { attendance } from "./schemas/attendance";
import { departments } from "./schemas/departments";
import { designations } from "./schemas/designations";
import { employees } from "./schemas/employees";
import { holidays } from "./schemas/holidays";
import { leaveAllocations } from "./schemas/leave-allocations";
import { payrollEntries } from "./schemas/payroll-entries";
import { salarySlips } from "./schemas/salary-slips";
import { leaveApplications } from "./schemas/leave-applications";
import { leaveTypes } from "./schemas/leave-types";
import { overtimeRequests } from "./schemas/overtime-requests";
import { salaryComponents } from "./schemas/salary-components";
import { salaryStructures } from "./schemas/salary-structures";

// ============================================================================
// HR module — registered doctypes.
//
// Doctype Admin Epic (#347), issue #423. Migrates the four numbering series
// previously seeded per-company in `modules/hr/seed.ts` into the doctype
// registry's `defaultSeries`. The platform boot loop in
// `apps/tenant-worker/src/middleware/auto-migrate.ts` reads
// `module.registeredDoctypes` across every enabled module and upserts the
// platform-default rows into `core_doctype_registry` +
// `core_numbering_series` (the latter with `company_id = NULL`).
// `findSeries()` falls back to those NULL rows when no per-company override
// exists, so the seed is transparent to existing callers and per-company
// running counters are preserved.
//
// Scope:
//   - `employee` (master-data): registry of staff records — symmetric to
//     `customer`/`supplier`. User-supplied `employee_code` on create today,
//     but the series is registered so future auto-numbering and the Doctype
//     Admin Naming tab work without manual setup.
//   - `payroll_entry` (transactional, monthly reset): one row per
//     payroll-run; numbered through `(MODULES.HR, "payroll_entry")` from
//     `routes/payroll-entries.ts:181`.
//   - `salary_slip` (transactional, monthly reset): one row per
//     payroll-line; numbered through `(MODULES.HR, "salary_slip")` from
//     `routes/payroll-entries.ts:315` (batched).
//   - `leave_application` (transactional, yearly reset): numbered through
//     `(MODULES.HR, "leave_application")` from
//     `routes/leave-applications.ts:227`.
// ============================================================================

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const READ_ONLY_DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

export const payrollEntryLifecycle = createSubmitCancelLifecycleContract({
  docStatusField: null,
  submittedStates: ["submitted", "paid"],
  cancelFromStates: ["submitted"],
  reversalPolicy: "reverse_gl",
  submitDocStatus: null,
  cancelDocStatus: null,
});

export const leaveApplicationLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "status",
    docStatusField: null,
    draftStates: ["draft"],
    submittedStates: ["pending", "approved"],
    cancelledStates: ["rejected", "cancelled"],
    immutableStates: ["approved", "rejected", "cancelled"],
    editAllowedStates: ["draft", "pending"],
    actions: {
      submit: {
        label: "ส่งอนุมัติ",
        from: ["draft"],
        to: "pending",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "submitted",
      },
      approve: {
        label: "อนุมัติ",
        from: ["pending"],
        to: "approved",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "approved",
      },
      reject: {
        label: "ปฏิเสธ",
        from: ["pending"],
        to: "rejected",
        docStatus: null,
        reversalPolicy: "status_only",
        requiresReason: true,
        activityAction: "rejected",
      },
    },
  });

export const overtimeRequestLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "status",
    docStatusField: null,
    draftStates: ["pending"],
    submittedStates: ["approved"],
    cancelledStates: ["rejected"],
    immutableStates: ["approved", "rejected"],
    editAllowedStates: ["pending"],
    actions: {
      submit: {
        label: "อนุมัติ",
        from: ["pending"],
        to: "approved",
        docStatus: null,
        reversalPolicy: "none",
        activityAction: "approved",
      },
      reject: {
        label: "ปฏิเสธ",
        from: ["pending"],
        to: "rejected",
        docStatus: null,
        reversalPolicy: "status_only",
        requiresReason: true,
        activityAction: "rejected",
      },
    },
  });

const CHILD_TABLE_CATALOG = (
  parentDoctype: string,
  parentField: string,
  parentKeyField: string,
  sortField?: string,
  cascadePolicy: DoctypeChildCascadePolicy = "restrict",
): DoctypeCatalogMetadata => ({
  childTable: {
    parentDoctype,
    parentField,
    parentKeyField,
    sortField: sortField ?? null,
    cascadePolicy,
  },
  policy: {
    isExportable: true,
    isReportable: true,
  },
});

/**
 * Employee (`พนักงาน`) — master-data doctype symmetric to sales' `customer`
 * and purchase's `supplier`. Stored in HR-owned `hr_employees`; numbering
 * surfaces on `employee_code`.
 *
 * Today the live create path (`routes/employees.ts`) accepts a
 * caller-supplied `employeeCode` — the doctype registration does NOT change
 * that. It seeds `core_numbering_series` with
 * `(module=hr, doctype=employee, company_id=NULL)` so future auto-numbering
 * (and the Doctype Admin Naming tab) can resolve a default series without
 * the admin having to create one by hand.
 *
 * `displayField` uses `first_name` because employees carry separate
 * first/last/nickname columns rather than a single combined `name` — the
 * Admin UI shows the first-name column as the primary identifier alongside
 * the code.
 */
export const employeeDoctype = defineDoctype({
  id: "employee",
  moduleId: "hr",
  table: employees,
  label: "พนักงาน",
  icon: "Users",
  features: [
    "naming",
    "custom_fields",
    "workflow",
    "print",
    "permissions",
    "automation",
    "list_view",
  ],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: "employee_code",
  displayField: "first_name",
  imageField: null,
  searchFields: ["employee_code", "first_name", "last_name", "nickname"],
  // Thai UI copy for user-facing columns. System/audit columns
  // (company_id, timestamps, deleted_*, user_id) are intentionally omitted.
  columnLabels: {
    employee_code: "รหัสพนักงาน",
    first_name: "ชื่อ",
    last_name: "นามสกุล",
    nickname: "ชื่อเล่น",
    id_card_number: "เลขบัตรประชาชน",
    date_of_birth: "วันเกิด",
    gender: "เพศ",
    nationality: "สัญชาติ",
    phone: "เบอร์โทร",
    email: "อีเมล",
    address: "ที่อยู่",
    department_id: "แผนก",
    designation_id: "ตำแหน่ง",
    employment_type: "ประเภทการจ้าง",
    join_date: "วันเริ่มงาน",
    confirm_date: "วันบรรจุ",
    resign_date: "วันลาออก",
    bank_account_no: "เลขบัญชีธนาคาร",
    bank_code: "รหัสธนาคาร",
    social_security_no: "เลขประกันสังคม",
    tax_id: "เลขผู้เสียภาษี",
    salary: "เงินเดือน",
    salary_structure_id: "โครงสร้างเงินเดือน",
    branch_id: "สาขา",
    status: "สถานะ",
  },
  defaultSeries: {
    prefix: "EMP-#####",
    resetEvery: "never",
    seriesName: "Standard",
  },
});

/**
 * Payroll Entry (`การจ่ายเงินเดือน`) — one row per payroll-run, numbered
 * monthly. Stored in HR-owned `hr_payroll_entries`; the live create path
 * `routes/payroll-entries.ts:181` calls
 * `generateNextNumber({ module: HR, doctype: "payroll_entry" })` and writes
 * the result into `payroll_no`.
 *
 * `defaultSeries.prefix` mirrors the legacy `seed.ts` value so existing
 * per-company counters keep their running value (per-company row wins over
 * the platform-default NULL fallback). `resetEvery: "monthly"` matches the
 * `{YYYY}-{MM}` segments in the prefix.
 */
export const payrollEntryDoctype = defineDoctype({
  id: "payroll_entry",
  moduleId: "hr",
  table: payrollEntries,
  label: "การจ่ายเงินเดือน",
  icon: "Wallet",
  features: [
    "naming",
    "custom_fields",
    "workflow",
    "print",
    "permissions",
    "automation",
    "list_view",
  ],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "payroll_no",
  displayField: "payroll_no",
  imageField: null,
  searchFields: ["payroll_no", "status"],
  columnLabels: {
    payroll_no: "เลขที่การจ่ายเงินเดือน",
    payroll_month: "เดือนที่จ่าย",
    status: "สถานะ",
    total_employees: "จำนวนพนักงาน",
    total_earnings: "รายได้รวม",
    total_deductions: "รายหักรวม",
    total_net_pay: "ยอดสุทธิรวม",
    payment_date: "วันที่จ่าย",
    payment_method: "วิธีการจ่าย",
  },
  defaultSeries: {
    prefix: "PAY-{YYYY}-{MM}-#####",
    resetEvery: "monthly",
    seriesName: "Standard",
  },
  lifecycle: payrollEntryLifecycle,
});

/**
 * Salary Slip (`สลิปเงินเดือน`) — one row per employee per payroll-run,
 * numbered monthly. Stored in HR-owned `hr_salary_slips`; the live create
 * path `routes/payroll-entries.ts:315` batches
 * `generateNextNumberBatch({ module: HR, doctype: "salary_slip" })` and
 * writes each result into `slip_no`.
 *
 * Prefix mirrors legacy `seed.ts`; `monthly` reset matches the `{YYYY}-{MM}`
 * segments. Per-company seeded rows continue to win over the NULL fallback.
 */
export const salarySlipDoctype = defineDoctype({
  id: "salary_slip",
  moduleId: "hr",
  table: salarySlips,
  label: "สลิปเงินเดือน",
  icon: "Wallet",
  features: [
    "naming",
    "custom_fields",
    "workflow",
    "print",
    "permissions",
    "automation",
    "list_view",
  ],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "slip_no",
  displayField: "slip_no",
  imageField: null,
  searchFields: ["slip_no", "employee_code", "employee_name"],
  columnLabels: {
    slip_no: "เลขที่สลิปเงินเดือน",
    payroll_entry_id: "รอบการจ่ายเงินเดือน",
    employee_id: "พนักงาน",
    employee_code: "รหัสพนักงาน",
    employee_name: "ชื่อพนักงาน",
    department_id: "แผนก",
    designation_id: "ตำแหน่ง",
    base_salary: "เงินเดือนฐาน",
    total_earnings: "รายได้รวม",
    total_deductions: "รายหักรวม",
    net_pay: "ยอดสุทธิ",
    income_tax: "ภาษีหัก ณ ที่จ่าย",
    social_security: "ประกันสังคม",
    provident_fund: "กองทุนสำรองเลี้ยงชีพ",
  },
  defaultSeries: {
    prefix: "SS-{YYYY}-{MM}-#####",
    resetEvery: "monthly",
    seriesName: "Standard",
  },
});

/**
 * Leave Application (`ใบลา`) — submittable doctype, numbered yearly. Stored
 * in HR-owned `hr_leave_applications`; the live create path
 * `routes/leave-applications.ts:227` calls
 * `generateNextNumber({ module: HR, doctype: "leave_application" })` and
 * writes the result into `application_no`.
 *
 * Prefix mirrors legacy `seed.ts`. `yearly` reset matches the `{YYYY}`
 * segment. Per-company seeded rows continue to win over the NULL fallback.
 */
export const leaveApplicationDoctype = defineDoctype({
  id: "leave_application",
  moduleId: "hr",
  table: leaveApplications,
  label: "ใบลา",
  icon: "CalendarDays",
  features: [
    "naming",
    "custom_fields",
    "workflow",
    "print",
    "permissions",
    "automation",
    "list_view",
  ],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "application_no",
  displayField: "application_no",
  imageField: null,
  searchFields: ["application_no", "status"],
  columnLabels: {
    application_no: "เลขที่ใบลา",
    employee_id: "พนักงาน",
    leave_type_id: "ประเภทวันลา",
    from_date: "ลาตั้งแต่",
    to_date: "ลาถึง",
    total_days: "จำนวนวัน",
    reason: "เหตุผล",
    status: "สถานะ",
    approved_by: "ผู้อนุมัติ",
    approved_at: "วันที่อนุมัติ",
    rejection_reason: "เหตุผลการปฏิเสธ",
  },
  defaultSeries: {
    prefix: "LA-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  lifecycle: leaveApplicationLifecycle,
});

export const departmentDoctype = defineDoctype({
  id: "department",
  moduleId: "hr",
  table: departments,
  label: "แผนก",
  icon: "Building2",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "parent_id"],
});

export const designationDoctype = defineDoctype({
  id: "designation",
  moduleId: "hr",
  table: designations,
  label: "ตำแหน่ง",
  icon: "IdCard",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
});

export const leaveTypeDoctype = defineDoctype({
  id: "leave_type",
  moduleId: "hr",
  table: leaveTypes,
  label: "ประเภทวันลา",
  icon: "CalendarDays",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
});

export const leaveAllocationDoctype = defineDoctype({
  id: "leave_allocation",
  moduleId: "hr",
  table: leaveAllocations,
  label: "สิทธิ์วันลา",
  icon: "CalendarCheck",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: READ_ONLY_DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "employee_id",
  imageField: null,
  searchFields: ["employee_id", "leave_type_id", "fiscal_year"],
});

export const holidayDoctype = defineDoctype({
  id: "holiday",
  moduleId: "hr",
  table: holidays,
  label: "วันหยุด",
  icon: "CalendarHeart",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "date"],
});

export const salaryStructureDoctype = defineDoctype({
  id: "salary_structure",
  moduleId: "hr",
  table: salaryStructures,
  label: "โครงสร้างเงินเดือน",
  icon: "ListTree",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "description"],
});

export const salaryComponentDoctype = defineDoctype({
  id: "salary_component",
  moduleId: "hr",
  table: salaryComponents,
  label: "รายการเงินเดือน",
  icon: "Rows3",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "child_table",
  catalog: CHILD_TABLE_CATALOG("salary_structure", "components", "salary_structure_id", "sort_order"),
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "type", "calculation_type"],
});

export const attendanceDoctype = defineDoctype({
  id: "attendance",
  moduleId: "hr",
  table: attendance,
  label: "บันทึกเวลา",
  icon: "Clock",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "date",
  imageField: null,
  searchFields: ["employee_id", "date", "status"],
});

export const overtimeRequestDoctype = defineDoctype({
  id: "overtime_request",
  moduleId: "hr",
  table: overtimeRequests,
  label: "ขอล่วงเวลา",
  icon: "Clock3",
  features: ["custom_fields", "workflow", "permissions", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "employee_id",
  imageField: null,
  searchFields: ["employee_id", "date", "status", "reason"],
  lifecycle: overtimeRequestLifecycle,
});

/**
 * Every doctype the HR module wants registered with the platform. The
 * order here is preserved in the Admin UI through `listDoctypes()` sort —
 * organization master data first, followed by employee, leave, salary,
 * payroll, attendance, and overtime surfaces.
 */
export const hrDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  departmentDoctype,
  designationDoctype,
  employeeDoctype,
  leaveTypeDoctype,
  leaveAllocationDoctype,
  holidayDoctype,
  salaryStructureDoctype,
  salaryComponentDoctype,
  payrollEntryDoctype,
  salarySlipDoctype,
  leaveApplicationDoctype,
  attendanceDoctype,
  overtimeRequestDoctype,
]);
